import { DavetYenidenGonder } from "./DavetYenidenGonder";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Ikon, Kart, Secim } from "@koclukpanel/ui";
import {
  ApiHatasi,
  adayGetir,
  adaylariGetir,
  benGetir,
  ogrenciOlustur,
  type OgrenciKayitSonucu,
} from "./api";
import { AlanSecimi } from "./AlanSecimi";
import {
  BOS_FORM,
  adayBilgileriniTasi,
  SINIFLAR,
  dogrula,
  govdeyeCevir,
  muvafakatGerekiyorMu,
  type KayitFormu,
  type Sinif,
} from "./ogrenci-kayit";

/**
 * ÖĞRENCİ KAYDI AÇMA — F-PPL-01
 *
 * ================================================================
 * SİSTEM BOŞ AÇILIYOR VE BU EKRAN İLK KAPI
 * ================================================================
 * 06.08.2026'ya kadar `POST /ogrenciler` ucu sunucuda vardı ama
 * panelde hiçbir yerden çağrılmıyordu — yani sisteme ilk öğrenci
 * arayüzden GİRİLEMİYORDU. Program, deneme, rapor, abonelik ve risk
 * ekranlarının hepsi öğrenciye bağlı olduğu için o ekranlar da hiç
 * denenemiyordu.
 *
 * ================================================================
 * MUVAFAKAT UYARISI ENGEL DEĞİL
 * ================================================================
 * F-PPL-05b: 18 yaş altı hesap, veli muvafakati kaydedilmeden
 * aktifleşmez. Form bunu ENGEL olarak kurmuyor — kayıt açılır, davet
 * gider, öğrenci şifresini belirler, yalnızca giriş yapamaz.
 *
 * Engel olsaydı 18 yaş altı öğrenci hiç kaydedilemezdi ve bu firmanın
 * öğrencilerinin çoğu 18 yaş altı. Doğru davranış uyarmak: kaydı açan
 * kişi, kaydı açarken muvafakatin gerekeceğini bilsin.
 */

/**
 * `ExamKind` — sunucudaki `SINAV_TURLERI` ile AYNI degerler.
 *
 * `ogrenci360/HedefFormu.tsx`te de ayni tablo var. Iki kopya sunucuda
 * bir dordunculuk uretmiyor (deger listesi Zod semasinda) ama bir gun
 * ayrisirlarsa iki ekran ayni sinavi baska adla yazar. Ucuncu bir
 * kopya cikarsa `packages/ui` ya da `contracts` altina tasinmali.
 */
const HEDEF_SINAV_ADI: Record<string, string> = {
  TYT: "TYT",
  AYT: "AYT",
  YDT: "YDT (Yabancı Dil)",
  LGS: "LGS",
  BRANCH: "Branş sınavı",
  KPSS: "KPSS",
  DGS: "DGS",
  OTHER: "Diğer",
};

const SINIF_ADI: Record<Sinif, string> = {
  G5: "5. sınıf",
  G6: "6. sınıf",
  G7: "7. sınıf",
  G8: "8. sınıf",
  G9: "9. sınıf",
  G10: "10. sınıf",
  G11: "11. sınıf",
  G12: "12. sınıf",
  GRADUATE: "Mezun",
  OTHER: "Diğer",
};

export function OgrenciYeni({
  onKapat,
  onAcildi,
}: {
  onKapat: () => void;
  /** Kayıt açılınca listeye dönmek isteyen ekran için. */
  onAcildi?: (sonuc: OgrenciKayitSonucu) => void;
}) {
  const [form, setForm] = useState<KayitFormu>(BOS_FORM);
  const [adayId, setAdayId] = useState("");
  const ben = useQuery({ queryKey: ["ben"], queryFn: benGetir });
  const adayGorebilir = Boolean(ben.data?.izinler["crm.aday.goruntule"]);
  const adaylar = useQuery({
    queryKey: ["kayit-adaylari"],
    queryFn: () => adaylariGetir(),
    enabled: adayGorebilir,
  });
  const aday = useQuery({
    queryKey: ["kayit-adayi", adayId],
    queryFn: () => adayGetir(adayId),
    enabled: adayGorebilir && Boolean(adayId),
  });
  useEffect(() => {
    if (aday.data?.id === adayId) setForm((f) => adayBilgileriniTasi(f, aday.data!));
  }, [aday.data, adayId]);
  /**
   * Hatalar YAZARKEN değil GÖNDERİRKEN gösteriliyor.
   *
   * Her tuşta hata basmak, adının ilk harfini yazan kişiye "Ad boş
   * olamaz" demek olurdu — doğru ama kaba ve okunmuyor.
   */
  const [denendi, setDenendi] = useState(false);
  /*
    HEDEF BOLUMU KAPALI BASLIYOR — F-PPL-05

    Emre'nin karari: hedef kayitta da girilebilsin. Ama kaydi acan
    kisi cogu zaman ILK GUN hedefi bilmiyor; bes alani zorunlu
    alanlarin arasina serpistirmek formu iki katina cikarir ve
    asil isi (kaydi acmak) gurultuye bogardi.

    Kapali baslamasi 'yok' demek DEGIL: basligi gorunuyor, yani
    hedefin buraya girilebilecegi biliniyor.
  */
  const [hedefAcik, setHedefAcik] = useState(false);
  const [sonuc, setSonuc] = useState<OgrenciKayitSonucu | null>(null);

  const qc = useQueryClient();
  const bugun = new Date();
  const hatalar = dogrula(form, bugun);
  const gecerli = Object.keys(hatalar).length === 0;
  const muvafakatUyarisi = muvafakatGerekiyorMu(form.dogumTarihi, bugun);

  const kaydet = useMutation({
    mutationFn: () => ogrenciOlustur({ ...govdeyeCevir(form), adayId: adayId || null }),
    onSuccess: async (s) => {
      setSonuc(s);
      setForm(BOS_FORM);
      setAdayId("");
      setDenendi(false);
      await qc.invalidateQueries({ queryKey: ["ogrenciler"] });
      await qc.invalidateQueries({ queryKey: ["kayit-adaylari"] });
      await qc.invalidateQueries({ queryKey: ["adaylar"] });
      await qc.invalidateQueries({ queryKey: ["huni"] });
      onAcildi?.(s);
    },
  });

  const alan = (k: keyof KayitFormu) => (denendi ? hatalar[k] : undefined);
  const yaz = (k: keyof KayitFormu, d: string | boolean) => setForm((f) => ({ ...f, [k]: d }));

  if (sonuc) {
    return (
      <Kart baslik="Öğrenci kaydı açıldı" ton={sonuc.muvafakatBekliyor ? "uyari" : "notr"}>
        <div className="flex flex-col gap-3">
          <p className="text-notr-700 flex items-center gap-2 text-sm">
            <Ikon ad="tamam" className="text-onay-600" />
            Öğrenci no <strong>{sonuc.studentNo}</strong> ile kaydedildi.
          </p>

          {sonuc.davetGonderildi === true && <p role="status">Davet gönderildi.</p>}
          {sonuc.davetGonderildi === false && (
            <p role="alert" className="text-uyari-800 text-sm">
              Davet postası gönderilemedi — yeniden gönderin
            </p>
          )}

          {sonuc.davetGonderildi === false && (
            <DavetYenidenGonder
              hedef={{ ogrenciId: sonuc.id }}
              izin="kisi.ogrenci.olustur"
              onGonderildi={() => setSonuc({ ...sonuc, davetGonderildi: true })}
            />
          )}

          {/*
            Bu uyarı SUNUCUNUN cevabından geliyor (`muvafakatBekliyor`),
            formun kendi yaş hesabından değil. İki taraf ayrıştığı gün
            doğru olan sunucudur.
          */}
          {sonuc.muvafakatBekliyor && (
            <p className="text-uyari-800 flex items-start gap-2 text-sm">
              <Ikon ad="uyari" className="text-uyari-600 mt-0.5" />
              <span>
                Öğrenci 18 yaşından küçük. Davet bağlantısıyla şifresini belirleyebilecek, ancak{" "}
                <strong>veli muvafakati kaydedilene kadar giriş yapamaz</strong>. Muvafakati öğrenci
                listesindeki &quot;İşlemler&quot; menüsünden kaydedin.
              </span>
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Buton onClick={() => setSonuc(null)}>Bir tane daha ekle</Buton>
            <Buton gorunum="ikincil" onClick={onKapat}>
              Listeye dön
            </Buton>
          </div>
        </div>
      </Kart>
    );
  }

  return (
    <Kart baslik="Yeni öğrenci">
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          setDenendi(true);
          if (gecerli && (!adayId || (aday.isSuccess && !aday.isFetching))) kaydet.mutate();
        }}
      >
        {/* F-CRM-08b — seçici yalnızca kapsamlı aday listesini kullanır. */}
        {adayGorebilir && (
          <>
            <Secim
              etiket="Aday bağlantısı (isteğe bağlı)"
              value={adayId}
              onChange={(e) => setAdayId(e.target.value)}
              disabled={kaydet.isPending}
            >
              <option value="">Aday bağlamadan kayıt aç</option>
              {adaylar.data?.adaylar
                .filter((a) => a.status !== "ENROLLED")
                .map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.studentName}
                  </option>
                ))}
            </Secim>
            {adaylar.isFetching && <p role="status">Aday listesi alınıyor…</p>}
            {adaylar.isError && (
              <p role="alert">
                Aday listesi alınamadı.{" "}
                <Buton gorunum="sessiz" onClick={() => void adaylar.refetch()}>
                  Tekrar dene
                </Buton>
              </p>
            )}
            {adayId && aday.isFetching && <p role="status">Aday bilgileri alınıyor…</p>}
            {adayId && aday.isError && (
              <p role="alert">
                Aday bilgileri alınamadı.{" "}
                <Buton gorunum="sessiz" onClick={() => void aday.refetch()}>
                  Tekrar dene
                </Buton>
              </p>
            )}
          </>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Alan
            etiket="Ad"
            zorunlu
            value={form.ad}
            onChange={(e) => yaz("ad", e.target.value)}
            hata={alan("ad")}
            autoComplete="off"
          />
          <Alan
            etiket="Soyad"
            zorunlu
            value={form.soyad}
            onChange={(e) => yaz("soyad", e.target.value)}
            hata={alan("soyad")}
            autoComplete="off"
          />
          <Alan
            etiket="E-posta"
            zorunlu
            type="email"
            value={form.eposta}
            onChange={(e) => yaz("eposta", e.target.value)}
            hata={alan("eposta")}
            yardim="Davet bağlantısı buraya gidecek."
            autoComplete="off"
          />
          <Alan
            etiket="Telefon"
            type="tel"
            value={form.telefon}
            onChange={(e) => yaz("telefon", e.target.value)}
            yardim="İsteğe bağlı."
            autoComplete="off"
          />
          {/*
            C39 — ortak numarada kimin telefonuyla gireceği.
            Kutu yalnızca telefon yazılınca görünüyor: telefonu olmayan
            birine "numaranın sahibi mi" diye sormak anlamsız ve
            işaretlenirse zaten reddediliyor.
          */}
          {form.telefon.trim().length > 0 && (
            <label className="text-notr-700 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.telefonSahibi}
                onChange={(e) => yaz("telefonSahibi", e.target.checked)}
                className="mt-0.5"
              />
              <span>
                Bu numaranın sahibi bu öğrenci
                <span className="text-notr-600 block text-xs">
                  Aynı numara veliyle paylaşılıyorsa telefonla girişi sahibi yapar; öteki kişi
                  e-postasıyla girer.
                </span>
              </span>
            </label>
          )}
          <Alan
            etiket="T.C. kimlik numarası"
            value={form.tcKimlik}
            onChange={(e) => yaz("tcKimlik", e.target.value)}
            hata={alan("tcKimlik")}
            yardim="İsteğe bağlı. Girilirse öğrenci bu numarayla da giriş yapabilir."
            autoComplete="off"
            inputMode="numeric"
          />
          <Alan
            etiket="Doğum tarihi"
            zorunlu
            type="date"
            value={form.dogumTarihi}
            onChange={(e) => yaz("dogumTarihi", e.target.value)}
            hata={alan("dogumTarihi")}
            // Neden zorunlu oldugu SORULMADAN once soyleniyor: bu alani
            // "gereksiz veri" diye bos birakma durtusu, 18 yas korumasini
            // sessizce devre disi birakirdi. "Zorunlu" sozcugu artik
            // etiketin yaninda (`zorunlu`); burada yalniz NEDENI kaliyor.
            yardim="18 yaş kuralı buna bağlı."
          />
          <Secim
            etiket="Sınıf"
            zorunlu
            value={form.sinif}
            onChange={(e) => yaz("sinif", e.target.value)}
            hata={alan("sinif")}
          >
            <option value="">Seçin…</option>
            {SINIFLAR.map((s) => (
              <option key={s} value={s}>
                {SINIF_ADI[s]}
              </option>
            ))}
          </Secim>
          {/* F-PPL-05c — program ekranı dersleri bu alandan getiriyor. */}
          <AlanSecimi deger={form.alan} onChange={(v) => yaz("alan", v)} />
        </div>

        {muvafakatUyarisi && (
          /*
            Kayit ACILMADAN once uyariyor. Sonradan soylemek de mumkundu
            ama o zaman kaydi acan kisi, muvafakat gerektigini ancak
            is bittikten sonra ogrenirdi.
          */
          <p className="rounded-kart bg-uyari-50 text-uyari-800 flex items-start gap-2 p-3 text-sm">
            <Ikon ad="uyari" className="text-uyari-600 mt-0.5" />
            <span>
              Girilen doğum tarihine göre öğrenci 18 yaşından küçük. Kayıt açılır ve davet gider,
              ancak <strong>veli muvafakati kaydedilene kadar giriş yapamaz</strong>
              {/* F-PPL-05b */}.
            </span>
          </p>
        )}

        <div className="border-notr-200 flex flex-col gap-3 border-t pt-4">
          {hedefAcik ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-notr-900 text-sm font-semibold">Hedef (isteğe bağlı)</h3>
                <Buton gorunum="ikincil" type="button" onClick={() => setHedefAcik(false)}>
                  Gizle
                </Buton>
              </div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Secim
                  etiket="Hedef sınav"
                  value={form.hedefSinav}
                  onChange={(e) => yaz("hedefSinav", e.target.value)}
                >
                  <option value="">Seçilmedi</option>
                  {Object.entries(HEDEF_SINAV_ADI).map(([deger, ad]) => (
                    <option key={deger} value={deger}>
                      {ad}
                    </option>
                  ))}
                </Secim>
                <Alan
                  etiket="Hedef puan"
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  value={form.hedefPuan}
                  onChange={(e) => yaz("hedefPuan", e.target.value)}
                />
                <Alan
                  etiket="Hedef sıralama"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min="1"
                  value={form.hedefSiralama}
                  onChange={(e) => yaz("hedefSiralama", e.target.value)}
                />
                <Alan
                  etiket="Hedef okul"
                  value={form.hedefOkul}
                  onChange={(e) => yaz("hedefOkul", e.target.value)}
                  maxLength={200}
                />
                <Alan
                  etiket="Sınav tarihi"
                  type="date"
                  value={form.sinavTarihi}
                  onChange={(e) => yaz("sinavTarihi", e.target.value)}
                />
              </div>
              <p className="text-notr-600 text-xs">
                Boş bırakılan alan <strong>gönderilmez</strong>; hedef sonradan öğrenci ekranından
                da girilebilir.
              </p>
            </>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-notr-700 text-sm">
                <strong className="font-medium">Hedef</strong> — sınav, puan, okul. İsteğe bağlı,
                sonradan da girilebilir.
              </p>
              <Buton gorunum="ikincil" type="button" onClick={() => setHedefAcik(true)}>
                Hedef ekle
              </Buton>
            </div>
          )}
        </div>

        <label className="text-notr-700 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.davetGonder}
            onChange={(e) => yaz("davetGonder", e.target.checked)}
            className="ring-notr-300 size-4 rounded ring-1"
          />
          Davet e-postası gönder
        </label>

        {kaydet.isError && (
          <p role="alert" className="text-hata-500 text-sm font-medium">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kayıt açılamadı."}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {/*
            `disabled` KULLANILMIYOR. Kapali bir dugme, NEDEN kapali
            oldugunu soylemiyor; kullanici tiklayamadigi bir dugmeye
            bakip forma geri donmek zorunda kaliyor. Basilinca hatalari
            gostermek daha hizli yol.
          */}
          <Buton type="submit" yukleniyor={kaydet.isPending}>
            Kaydı aç
          </Buton>
          <Buton type="button" gorunum="ikincil" onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}
